import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchCardDetailsPCICompliant, BridgeCardDetailsResponse } from 'services/bridgeApi';
import { formatErrorMessage } from 'utils/error';
import theraToast from 'components/generic-components/toast/TheraToast';

interface UseSecureCardDetailsProps {
  theraFernApi: any;
  customerId?: string;
  cardAccountId?: string;
}

interface UseSecureCardDetailsReturn {
  cardDetails: BridgeCardDetailsResponse | null;
  isLoadingCardDetails: boolean;
  fetchSecureCardDetails: () => Promise<void>;
}

/**
 * Custom hook for fetching card details securely using ephemeral keys
 * @param theraFernApi - The Thera Fern API client
 * @param customerId - The Bridge customer ID
 * @param cardAccountId - The card account ID
 * @returns Card details, loading state, and fetch function
 */
export const useSecureCardDetails = ({
  theraFernApi,
  customerId,
  cardAccountId,
}: UseSecureCardDetailsProps): UseSecureCardDetailsReturn => {
  const [cardDetails, setCardDetails] = useState<BridgeCardDetailsResponse | null>(null);
  const [isLoadingCardDetails, setIsLoadingCardDetails] = useState(Boolean(customerId && cardAccountId));
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchSecureCardDetails = useCallback(async () => {
    if (!customerId || !cardAccountId) return;

    try {
      setIsLoadingCardDetails(true);
      const secureCardDetails = await fetchCardDetailsPCICompliant({
        theraFernApi,
        customerId,
        cardAccountId,
      });
      if (mountedRef.current) {
        setCardDetails(secureCardDetails);
      }
    } catch (error) {
      if (mountedRef.current) {
        const errorMessage = formatErrorMessage(error, 'Failed to fetch card details');
        theraToast.error({
          message: errorMessage,
          error: error,
          options: {
            id: 'failed-to-fetch-card-details',
          },
        });
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingCardDetails(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, cardAccountId, theraFernApi]);

  // Auto-fetch card details on mount
  useEffect(() => {
    if (customerId && cardAccountId && !cardDetails) {
      fetchSecureCardDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, cardAccountId]);

  return {
    cardDetails,
    isLoadingCardDetails,
    fetchSecureCardDetails,
  };
};
